<template>
  <div class="mobile-portal">
    <header class="portal-bar">
      <button class="brand-button" type="button" aria-label="RoboCoin H5 首页" @click="goHomeView">
        <img src="../images/robocoin-logo-mark.png" alt="" aria-hidden="true" />
        <span>
          <strong>RoboCoin DeRAS</strong>
          <small>Agent Field Console</small>
        </span>
      </button>
      <span :class="['mode-status', { live: isAuthenticated }]">
        <i></i>
        {{ isAuthenticated ? 'Network Live' : (previewMode ? 'UI Preview' : 'Offline') }}
      </span>
    </header>

    <main class="portal-shell">
      <section v-if="!hasPortalAccess" class="auth-view" aria-labelledby="mobile-auth-title">
        <div class="auth-visual">
          <img :src="heroImage" alt="自主机器人执行服务任务" />
          <div class="auth-visual-mask">
            <span class="section-code">MOBILE ACCESS / DERAS</span>
            <h1 id="mobile-auth-title">连接你的真实世界任务</h1>
            <p>发布任务、查看机器人在线状态，并在移动端跟进执行进度。</p>
          </div>
          <div class="scan-line"></div>
        </div>

        <div v-if="serviceError" class="service-alert" role="alert">
          <el-icon><WarningFilled /></el-icon>
          <div>
            <strong>服务暂不可用</strong>
            <span>{{ serviceError }}</span>
          </div>
        </div>

        <div class="auth-tabs" role="tablist" aria-label="登录或注册">
          <button
            type="button"
            role="tab"
            :aria-selected="authTab === 'login'"
            :class="{ active: authTab === 'login' }"
            @click="switchAuthTab('login')"
          >
            登录
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="authTab === 'register'"
            :class="{ active: authTab === 'register' }"
            @click="switchAuthTab('register')"
          >
            注册
          </button>
        </div>

        <form v-if="authTab === 'login'" class="auth-form" @submit.prevent="submitLogin">
          <label>
            <span>邮箱或国际手机号</span>
            <input
              v-model.trim="loginForm.account"
              type="text"
              autocomplete="username"
              placeholder="name@example.com / +86138..."
            />
          </label>
          <label>
            <span>密码</span>
            <div class="password-field">
              <input
                v-model="loginForm.password"
                :type="loginPasswordVisible ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="请输入密码"
              />
              <button
                type="button"
                :aria-label="loginPasswordVisible ? '隐藏密码' : '显示密码'"
                :title="loginPasswordVisible ? '隐藏密码' : '显示密码'"
                @click="loginPasswordVisible = !loginPasswordVisible"
              >
                <el-icon><Hide v-if="loginPasswordVisible" /><View v-else /></el-icon>
              </button>
            </div>
          </label>
          <button class="submit-button" type="submit" :disabled="submitting">
            <el-icon><Connection /></el-icon>
            {{ submitting ? '正在建立连接...' : '进入移动工作台' }}
          </button>
        </form>

        <form v-else class="auth-form" @submit.prevent="submitRegister">
          <label>
            <span>显示名称</span>
            <input v-model.trim="registerForm.username" type="text" maxlength="100" placeholder="你的名称" />
          </label>
          <label>
            <span>邮箱</span>
            <input v-model.trim="registerForm.email" type="email" autocomplete="email" placeholder="name@example.com" />
          </label>
          <div class="phone-fields">
            <label>
              <span>区号</span>
              <input v-model.trim="registerForm.phoneCountryCode" type="text" inputmode="tel" placeholder="+86" />
            </label>
            <label>
              <span>手机号</span>
              <input v-model.trim="registerForm.phone" type="tel" autocomplete="tel" placeholder="138..." />
            </label>
          </div>
          <label>
            <span>密码</span>
            <div class="password-field">
              <input
                v-model="registerForm.password"
                :type="registerPasswordVisible ? 'text' : 'password'"
                autocomplete="new-password"
                placeholder="至少 8 位"
              />
              <button
                type="button"
                :aria-label="registerPasswordVisible ? '隐藏密码' : '显示密码'"
                :title="registerPasswordVisible ? '隐藏密码' : '显示密码'"
                @click="registerPasswordVisible = !registerPasswordVisible"
              >
                <el-icon><Hide v-if="registerPasswordVisible" /><View v-else /></el-icon>
              </button>
            </div>
          </label>
          <label>
            <span>确认密码</span>
            <input
              v-model="registerForm.confirmPassword"
              type="password"
              autocomplete="new-password"
              placeholder="再次输入密码"
            />
          </label>
          <button class="submit-button" type="submit" :disabled="submitting">
            <el-icon><Connection /></el-icon>
            {{ submitting ? '正在创建...' : '创建开发者账户' }}
          </button>
        </form>

        <div class="preview-access">
          <span>暂时没有可用账户？</span>
          <button type="button" @click="enterPreviewMode">仅预览界面</button>
        </div>
      </section>

      <template v-else>
        <section v-if="activeView === 'home'" class="home-view">
          <div class="mission-hero">
            <img :src="heroImage" alt="机器人现场服务" />
            <div class="mission-mask">
              <div class="hero-status">
                <span><i></i> DERAS MISSION NETWORK</span>
                <strong>{{ todayText }}</strong>
              </div>
              <div class="hero-copy">
                <span class="section-code">FIELD COMMAND</span>
                <h1>{{ greeting }}，{{ displayName }}</h1>
                <p>{{ previewMode ? '当前为界面预览，登录后才能发布真实任务。' : '账户与电脑端保持同一套任务和机器人数据。' }}</p>
              </div>
            </div>
            <button class="avatar-button" type="button" aria-label="打开个人信息" @click="activeView = 'profile'">
              {{ avatarLetter }}
            </button>
          </div>

          <div v-if="previewMode" class="preview-banner">
            <el-icon><InfoFilled /></el-icon>
            <span>预览模式不会写入平台。登录后，手机发布的任务会同步出现在电脑端。</span>
          </div>

          <div v-if="dataError" class="data-alert" role="alert">
            <span>{{ dataError }}</span>
            <button type="button" :disabled="dataLoading" @click="loadPortalData">重试</button>
          </div>

          <section class="command-section">
            <div class="section-heading">
              <div>
                <span class="section-code">QUICK COMMAND</span>
                <h2>任务控制</h2>
              </div>
              <span>{{ dataLoading ? '同步中' : '实时数据' }}</span>
            </div>

            <button class="primary-command" type="button" @click="openTaskPublish">
              <span class="command-icon"><el-icon><Promotion /></el-icon></span>
              <span class="command-copy">
                <small>MISSION / 01</small>
                <strong>发布新任务</strong>
                <em>地图选点、任务分包、预估与正式提交</em>
              </span>
              <span class="command-arrow"><el-icon><ArrowRight /></el-icon></span>
            </button>

            <div class="secondary-commands">
              <button type="button" @click="activeView = 'robot'">
                <el-icon><Monitor /></el-icon>
                <span>
                  <small>ROBOTS</small>
                  <strong>{{ robotCount }} 台设备</strong>
                </span>
                <i :class="{ online: onlineRobotCount > 0 }"></i>
              </button>
              <button type="button" @click="activeView = 'profile'">
                <el-icon><Document /></el-icon>
                <span>
                  <small>MISSIONS</small>
                  <strong>{{ taskCount }} 条任务</strong>
                </span>
                <el-icon><ArrowRight /></el-icon>
              </button>
            </div>
          </section>

          <section class="telemetry-strip" aria-label="工作台状态">
            <article>
              <span>SDK ONLINE</span>
              <strong>{{ onlineRobotCount }}</strong>
              <i class="cyan"></i>
            </article>
            <article>
              <span>ACCEPTING</span>
              <strong>{{ acceptingRobotCount }}</strong>
              <i class="green"></i>
            </article>
            <article>
              <span>MISSIONS</span>
              <strong>{{ taskCount }}</strong>
              <i class="amber"></i>
            </article>
          </section>

          <section class="activity-section">
            <div class="section-heading">
              <div>
                <span class="section-code">RECENT ACTIVITY</span>
                <h2>最近动态</h2>
              </div>
              <button class="icon-button" type="button" title="刷新" aria-label="刷新动态" :disabled="dataLoading" @click="loadPortalData">
                <el-icon><Refresh /></el-icon>
              </button>
            </div>
            <div v-if="activities.length" class="activity-list">
              <article v-for="item in activities" :key="item.key">
                <span :class="['activity-mark', item.type]">{{ item.short }}</span>
                <div>
                  <strong>{{ item.title }}</strong>
                  <small>{{ item.meta }}</small>
                </div>
                <em>{{ item.status }}</em>
              </article>
            </div>
            <div v-else class="empty-state">
              <el-icon><Document /></el-icon>
              <strong>{{ dataLoading ? '正在同步数据' : '暂无任务动态' }}</strong>
              <span>{{ previewMode ? '登录后显示账户中的真实记录。' : '发布首条任务后，状态会显示在这里。' }}</span>
            </div>
          </section>
        </section>

        <section v-else-if="activeView === 'robot'" class="robot-view">
          <div class="view-heading">
            <button type="button" aria-label="返回 H5 首页" title="返回" @click="activeView = 'home'">
              <el-icon><ArrowLeft /></el-icon>
            </button>
            <div>
              <span class="section-code">ROBOT CENTER</span>
              <h1>机器人设备</h1>
            </div>
            <button class="icon-button" type="button" title="刷新" aria-label="刷新机器人" :disabled="dataLoading" @click="loadPortalData">
              <el-icon><Refresh /></el-icon>
            </button>
          </div>

          <section class="robot-summary">
            <div>
              <span>REGISTERED</span>
              <strong>{{ robotCount }}</strong>
            </div>
            <div>
              <span>SDK ONLINE</span>
              <strong>{{ onlineRobotCount }}</strong>
            </div>
            <div>
              <span>ACCEPTING</span>
              <strong>{{ acceptingRobotCount }}</strong>
            </div>
          </section>

          <div class="desktop-registration">
            <el-icon><Monitor /></el-icon>
            <div>
              <strong>完整注册与 SDK 安装</strong>
              <span>请在电脑端机器人中心完成设备资料、钱包及 SDK 绑定。</span>
            </div>
            <button type="button" @click="goDesktopRobotCenter">电脑端入口</button>
          </div>

          <div v-if="robots.length" class="robot-list">
            <article v-for="robot in robots" :key="robot.id">
              <header>
                <span class="robot-glyph"><el-icon><Monitor /></el-icon></span>
                <div>
                  <strong>{{ robot.name || '未命名机器人' }}</strong>
                  <small>{{ robotCategoryLabel(robot.category) }} · ID {{ robot.id }}</small>
                </div>
                <em :class="robotStatusClass(robot.registerStatus)">{{ robotStatusLabel(robot.registerStatus) }}</em>
              </header>
              <div class="robot-signals">
                <span :class="{ active: robot.sdkAgentOnline }"><i></i>{{ robot.sdkAgentOnline ? 'SDK 在线' : 'SDK 离线' }}</span>
                <span :class="{ active: robot.acceptingOrders !== 0 }"><i></i>{{ robot.acceptingOrders !== 0 ? '开放接单' : '暂停接单' }}</span>
              </div>
              <p>{{ robot.capabilityDesc || '暂未填写能力描述。' }}</p>
            </article>
          </div>
          <div v-else class="empty-state robot-empty">
            <el-icon><Monitor /></el-icon>
            <strong>{{ dataLoading ? '正在读取设备' : '账户下暂无机器人' }}</strong>
            <span>{{ previewMode ? '预览模式不读取真实账户数据。' : '请在电脑端完成机器人注册和 SDK 绑定。' }}</span>
            <button v-if="!previewMode" type="button" @click="goDesktopRobotCenter">打开电脑端机器人中心</button>
          </div>
        </section>

        <section v-else class="profile-view">
          <div class="view-heading">
            <button type="button" aria-label="返回 H5 首页" title="返回" @click="activeView = 'home'">
              <el-icon><ArrowLeft /></el-icon>
            </button>
            <div>
              <span class="section-code">ACCOUNT NODE</span>
              <h1>账户中心</h1>
            </div>
          </div>

          <div class="profile-identity">
            <span>{{ avatarLetter }}</span>
            <div>
              <small>DEVELOPER IDENTITY</small>
              <strong>{{ displayName }}</strong>
              <em>{{ identityText }}</em>
            </div>
            <i :class="{ live: isAuthenticated }"></i>
          </div>

          <div class="profile-metrics">
            <article>
              <span>已发布任务</span>
              <strong>{{ taskCount }}</strong>
            </article>
            <article>
              <span>已注册机器人</span>
              <strong>{{ robotCount }}</strong>
            </article>
          </div>

          <dl class="account-list">
            <div>
              <dt>连接状态</dt>
              <dd>{{ isAuthenticated ? '已连接正式账户' : '界面预览' }}</dd>
            </div>
            <div>
              <dt>邮箱</dt>
              <dd>{{ currentUser.email || '未绑定' }}</dd>
            </div>
            <div>
              <dt>手机号</dt>
              <dd>{{ currentUser.phone || '未绑定' }}</dd>
            </div>
          </dl>

          <button class="logout-button" type="button" @click="exitPortal">
            <el-icon><SwitchButton /></el-icon>
            {{ isAuthenticated ? '退出登录' : '退出预览' }}
          </button>
        </section>
      </template>
    </main>

    <nav v-if="hasPortalAccess" class="bottom-nav" aria-label="H5 主要导航">
      <button type="button" :class="{ active: activeView === 'home' }" @click="activeView = 'home'">
        <el-icon><HomeFilled /></el-icon>
        <span>控制台</span>
      </button>
      <button type="button" :class="{ active: activeView === 'robot' }" @click="activeView = 'robot'">
        <el-icon><Monitor /></el-icon>
        <span>机器人</span>
      </button>
      <button type="button" :class="{ active: activeView === 'profile' }" @click="activeView = 'profile'">
        <el-icon><User /></el-icon>
        <span>我的</span>
      </button>
    </nav>

    <transition name="toast-pop">
      <div v-if="toastMessage" class="portal-toast" role="status">{{ toastMessage }}</div>
    </transition>
  </div>
</template>

<script>
import {
  ArrowLeft,
  ArrowRight,
  Connection,
  Document,
  Hide,
  HomeFilled,
  InfoFilled,
  Monitor,
  Promotion,
  Refresh,
  SwitchButton,
  User,
  View,
  WarningFilled
} from '@element-plus/icons-vue'
import { loginUser, registerUser } from '../api/auth'
import { getApiErrorMessage } from '../api/http'
import { getMyRegisteredRobots } from '../api/robot-register'
import { getMyPublishedTasks } from '../api/task-publish'
import heroImage from '../images/faburwu.png'
import {
  getAuthUser,
  getToken,
  logout,
  setAuthRole,
  setAuthUser,
  setToken
} from '../utils/auth'

const PREVIEW_KEY = 'roc_mobile_preview'

export default {
  name: 'MobilePortal',
  components: {
    ArrowLeft,
    ArrowRight,
    Connection,
    Document,
    Hide,
    HomeFilled,
    InfoFilled,
    Monitor,
    Promotion,
    Refresh,
    SwitchButton,
    User,
    View,
    WarningFilled
  },
  data() {
    const previewMode = window.sessionStorage.getItem(PREVIEW_KEY) === '1'
    return {
      heroImage,
      authTab: 'login',
      activeView: 'home',
      submitting: false,
      dataLoading: false,
      serviceError: '',
      dataError: '',
      toastMessage: '',
      toastTimer: null,
      authToken: getToken() || '',
      loginPasswordVisible: false,
      registerPasswordVisible: false,
      previewMode,
      currentUser: getAuthUser() || (previewMode ? { username: 'Preview User' } : {}),
      tasks: [],
      robots: [],
      loginForm: {
        account: '',
        password: ''
      },
      registerForm: {
        username: '',
        email: '',
        phoneCountryCode: '+86',
        phone: '',
        password: '',
        confirmPassword: ''
      }
    }
  },
  computed: {
    isAuthenticated() {
      return Boolean(this.authToken && this.currentUser && this.currentUser.id)
    },
    hasPortalAccess() {
      return this.isAuthenticated || this.previewMode
    },
    displayName() {
      return this.currentUser.username || this.currentUser.email || this.currentUser.phone || 'Developer'
    },
    identityText() {
      return this.currentUser.email || this.currentUser.phone || (this.previewMode ? 'UI Preview Account' : 'DeRAS Developer')
    },
    avatarLetter() {
      return String(this.displayName || 'R').trim().charAt(0).toUpperCase()
    },
    greeting() {
      const hour = new Date().getHours()
      if (hour < 11) return '早上好'
      if (hour < 18) return '下午好'
      return '晚上好'
    },
    todayText() {
      return new Intl.DateTimeFormat('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        weekday: 'short'
      }).format(new Date())
    },
    robotCount() {
      return this.robots.length
    },
    taskCount() {
      return this.tasks.length
    },
    onlineRobotCount() {
      return this.robots.filter(robot => Boolean(robot.sdkAgentOnline)).length
    },
    acceptingRobotCount() {
      return this.robots.filter(robot => robot.acceptingOrders !== 0 && robot.registerStatus === 'APPROVED').length
    },
    activities() {
      const robots = this.robots.map(robot => ({
        key: `robot-${robot.id}`,
        type: 'robot',
        short: '机',
        title: robot.name || '未命名机器人',
        meta: `${this.robotCategoryLabel(robot.category)} · ${this.formatDate(robot.updateTime || robot.createTime || robot.registerTime)}`,
        status: robot.sdkAgentOnline ? '在线' : this.robotStatusLabel(robot.registerStatus),
        timestamp: this.toTimestamp(robot.updateTime || robot.createTime || robot.registerTime)
      }))
      const tasks = this.tasks.map(task => ({
        key: `task-${task.id}`,
        type: 'task',
        short: '任',
        title: task.title || task.taskName || '未命名任务',
        meta: `${task.serviceAddress || task.beginAddress || '地点待确认'} · ${this.formatDate(task.updateTime || task.createTime || task.scheduleTime)}`,
        status: this.taskStatusLabel(task.publishStatus),
        timestamp: this.toTimestamp(task.updateTime || task.createTime || task.scheduleTime)
      }))
      return [...robots, ...tasks].sort((a, b) => b.timestamp - a.timestamp).slice(0, 5)
    }
  },
  created() {
    if (this.isAuthenticated) this.loadPortalData()
  },
  beforeUnmount() {
    if (this.toastTimer) window.clearTimeout(this.toastTimer)
  },
  methods: {
    switchAuthTab(tab) {
      this.authTab = tab
      this.serviceError = ''
    },
    validateLogin() {
      if (!this.loginForm.account) return '请输入邮箱或国际手机号。'
      if (!this.loginForm.password) return '请输入密码。'
      return ''
    },
    validateRegister() {
      if (!this.registerForm.email && !this.registerForm.phone) return '请填写邮箱或手机号。'
      if (this.registerForm.phone && !/^\+[1-9]\d{0,3}$/.test(this.registerForm.phoneCountryCode)) return '请输入正确的国际区号。'
      if (!this.registerForm.password || this.registerForm.password.length < 8) return '密码至少需要 8 位。'
      if (this.registerForm.password !== this.registerForm.confirmPassword) return '两次输入的密码不一致。'
      return ''
    },
    async submitLogin() {
      const validation = this.validateLogin()
      if (validation) {
        this.showToast(validation)
        return
      }
      this.submitting = true
      this.serviceError = ''
      try {
        const response = await loginUser(this.loginForm)
        if (!response.data || response.data.code !== 200) {
          this.serviceError = response.data && response.data.msg ? response.data.msg : '账号或密码不正确。'
          return
        }
        this.finishAuth(response.data, this.loginForm.account)
      } catch (error) {
        this.serviceError = getApiErrorMessage(error, '当前无法连接登录接口，请稍后重试。')
      } finally {
        this.submitting = false
      }
    },
    async submitRegister() {
      const validation = this.validateRegister()
      if (validation) {
        this.showToast(validation)
        return
      }
      this.submitting = true
      this.serviceError = ''
      try {
        const response = await registerUser({
          username: this.registerForm.username,
          email: this.registerForm.email,
          phoneCountryCode: this.registerForm.phoneCountryCode,
          phone: this.registerForm.phone,
          password: this.registerForm.password
        })
        if (!response.data || response.data.code !== 200) {
          this.serviceError = response.data && response.data.msg ? response.data.msg : '账户创建失败，请检查填写内容。'
          return
        }
        this.finishAuth(response.data, this.registerForm.username || this.registerForm.email || this.registerForm.phone)
      } catch (error) {
        this.serviceError = getApiErrorMessage(error, '当前无法连接注册接口，请稍后重试。')
      } finally {
        this.submitting = false
      }
    },
    finishAuth(payload, fallbackName) {
      const data = payload && payload.data ? payload.data : {}
      if (!data.token) {
        this.serviceError = '登录接口未返回有效凭证。'
        return
      }
      const user = data.user || { username: fallbackName, role: 'user' }
      if (!user.id) {
        this.serviceError = '登录接口未返回用户 ID，无法读取账户任务。'
        return
      }
      setToken(data.token)
      setAuthUser(user)
      setAuthRole('user')
      window.sessionStorage.removeItem(PREVIEW_KEY)
      this.previewMode = false
      this.authToken = data.token
      this.currentUser = user
      this.activeView = 'home'
      this.showToast('已连接 DeRAS 任务网络。')
      this.loadPortalData()
    },
    enterPreviewMode() {
      window.sessionStorage.setItem(PREVIEW_KEY, '1')
      this.previewMode = true
      this.currentUser = {
        username: this.registerForm.username || 'Preview User',
        email: this.registerForm.email || ''
      }
      this.tasks = []
      this.robots = []
      this.serviceError = ''
      this.dataError = ''
      this.activeView = 'home'
    },
    async loadPortalData() {
      if (!this.isAuthenticated) {
        this.tasks = []
        this.robots = []
        return
      }
      this.dataLoading = true
      this.dataError = ''
      const results = await Promise.allSettled([
        getMyPublishedTasks(this.currentUser.id),
        getMyRegisteredRobots(this.currentUser.id)
      ])
      const taskResult = results[0]
      const robotResult = results[1]
      const errors = []

      if (taskResult.status === 'fulfilled' && taskResult.value.data && taskResult.value.data.code === 200) {
        this.tasks = taskResult.value.data.data || []
      } else {
        errors.push(taskResult.status === 'rejected'
          ? getApiErrorMessage(taskResult.reason, '任务数据读取失败。')
          : ((taskResult.value.data && taskResult.value.data.msg) || '任务数据读取失败。'))
      }

      if (robotResult.status === 'fulfilled' && robotResult.value.data && robotResult.value.data.code === 200) {
        this.robots = (robotResult.value.data.data || []).filter(robot => robot.registerStatus !== 'CANCELLED')
      } else {
        errors.push(robotResult.status === 'rejected'
          ? getApiErrorMessage(robotResult.reason, '机器人数据读取失败。')
          : ((robotResult.value.data && robotResult.value.data.msg) || '机器人数据读取失败。'))
      }

      this.dataError = [...new Set(errors)].join(' ')
      this.dataLoading = false
    },
    goHomeView() {
      if (this.hasPortalAccess) this.activeView = 'home'
    },
    openTaskPublish() {
      this.$router.push({ name: 'mobileTaskPublish' })
    },
    goDesktopRobotCenter() {
      window.location.href = '/#/user'
    },
    exitPortal() {
      if (this.authToken) logout()
      window.sessionStorage.removeItem(PREVIEW_KEY)
      this.previewMode = false
      this.authToken = ''
      this.currentUser = {}
      this.tasks = []
      this.robots = []
      this.activeView = 'home'
      this.authTab = 'login'
      this.serviceError = ''
      this.dataError = ''
      this.loginForm.password = ''
    },
    robotCategoryLabel(value) {
      const labels = {
        WHEELED: '轮式机器人',
        CLEANING: '清洁机器人',
        DELIVERY: '配送机器人',
        DRONE: '无人机',
        SHUTTLE: '接驳机器人',
        INSPECTION: '巡检机器人',
        GO2: 'Unitree Go2'
      }
      return labels[String(value || '').toUpperCase()] || value || '未分类'
    },
    robotStatusLabel(value) {
      const labels = {
        APPROVED: '已通过',
        PENDING_REVIEW: '待验证',
        REJECTED: '未通过',
        CANCELLED: '已取消'
      }
      return labels[String(value || '').toUpperCase()] || value || '待注册'
    },
    robotStatusClass(value) {
      const status = String(value || '').toUpperCase()
      if (status === 'APPROVED') return 'approved'
      if (status === 'REJECTED' || status === 'CANCELLED') return 'rejected'
      return 'pending'
    },
    taskStatusLabel(value) {
      const labels = {
        ACTIVE: '等待接单',
        PENDING_REVIEW: '待审核',
        ACCEPTED: '已接单',
        IN_PROGRESS: '执行中',
        FINISHED: '已完成',
        CANCELLED: '已取消',
        REJECTED: '未通过'
      }
      return labels[String(value || '').toUpperCase()] || value || '已发布'
    },
    toTimestamp(value) {
      const timestamp = value ? new Date(String(value).replace(' ', 'T')).getTime() : 0
      return Number.isFinite(timestamp) ? timestamp : 0
    },
    formatDate(value) {
      const timestamp = this.toTimestamp(value)
      if (!timestamp) return '时间待同步'
      return new Intl.DateTimeFormat('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(timestamp))
    },
    showToast(message) {
      this.toastMessage = message
      if (this.toastTimer) window.clearTimeout(this.toastTimer)
      this.toastTimer = window.setTimeout(() => {
        this.toastMessage = ''
        this.toastTimer = null
      }, 2800)
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

button,
input {
  font: inherit;
  letter-spacing: 0;
}

button {
  -webkit-tap-highlight-color: transparent;
}

.mobile-portal {
  min-height: 100vh;
  padding-bottom: 82px;
  color: #edf7f3;
  background-color: #070b0a;
  background-image:
    linear-gradient(rgba(93, 231, 205, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(93, 231, 205, 0.035) 1px, transparent 1px);
  background-size: 28px 28px;
  font-family: "Noto Sans", system-ui, sans-serif;
}

.portal-bar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 62px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(128, 244, 214, 0.14);
  background: rgba(7, 11, 10, 0.92);
  backdrop-filter: blur(16px);
}

.brand-button {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 0;
  border: 0;
  color: inherit;
  text-align: left;
  background: transparent;
}

.brand-button img {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  object-fit: cover;
  background: #ffffff;
}

.brand-button strong,
.brand-button small {
  display: block;
}

.brand-button strong {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-button small {
  margin-top: 2px;
  color: rgba(237, 247, 243, 0.46);
  font-size: 8px;
  text-transform: uppercase;
}

.mode-status {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 7px;
  border: 1px solid rgba(246, 182, 72, 0.34);
  border-radius: 4px;
  color: #f6b648;
  font-size: 8px;
  font-weight: 850;
  text-transform: uppercase;
}

.mode-status i,
.hero-status i,
.robot-signals i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 10px currentColor;
}

.mode-status.live {
  color: #65e0ad;
  border-color: rgba(101, 224, 173, 0.36);
}

.portal-shell {
  width: min(100%, 480px);
  margin: 0 auto;
  padding: 16px 14px 28px;
}

.section-code {
  display: block;
  margin-bottom: 5px;
  color: #61d9c8;
  font-size: 9px;
  font-weight: 900;
  text-transform: uppercase;
}

.auth-visual,
.mission-hero {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(128, 244, 214, 0.17);
  border-radius: 8px;
  background: #101715;
}

.auth-visual {
  min-height: 270px;
}

.auth-visual > img,
.mission-hero > img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.auth-visual > img {
  object-position: 43% center;
}

.auth-visual-mask,
.mission-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 18px;
  background: linear-gradient(180deg, rgba(4, 8, 7, 0.08) 18%, rgba(4, 8, 7, 0.92) 100%);
}

.auth-visual h1,
.mission-hero h1,
.view-heading h1,
.section-heading h2 {
  margin: 0;
  letter-spacing: 0;
}

.auth-visual h1 {
  max-width: 300px;
  font-size: 26px;
  line-height: 1.24;
}

.auth-visual p {
  max-width: 310px;
  margin: 8px 0 0;
  color: rgba(237, 247, 243, 0.7);
  font-size: 12px;
  line-height: 1.6;
}

.scan-line {
  position: absolute;
  top: 0;
  right: 14px;
  width: 1px;
  height: 100%;
  background: rgba(101, 224, 173, 0.48);
  box-shadow: 0 0 12px #65e0ad;
  animation: scan 4.6s ease-in-out infinite alternate;
}

@keyframes scan {
  from { transform: translateX(-310px); }
  to { transform: translateX(0); }
}

.service-alert,
.preview-banner,
.data-alert {
  margin-top: 12px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.55;
}

.service-alert,
.preview-banner {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 9px;
  align-items: start;
  padding: 11px;
}

.service-alert {
  color: #ffc1ae;
  border: 1px solid rgba(245, 117, 78, 0.35);
  background: rgba(110, 31, 18, 0.34);
}

.service-alert strong,
.service-alert span {
  display: block;
}

.service-alert span {
  margin-top: 2px;
  color: rgba(255, 213, 201, 0.72);
}

.auth-tabs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  margin-top: 18px;
  border-bottom: 1px solid rgba(128, 244, 214, 0.15);
}

.auth-tabs button {
  height: 40px;
  border: 0;
  border-bottom: 2px solid transparent;
  color: rgba(237, 247, 243, 0.48);
  font-weight: 750;
  background: transparent;
}

.auth-tabs button.active {
  color: #edf7f3;
  border-bottom-color: #61d9c8;
}

.auth-form {
  display: grid;
  gap: 14px;
  margin-top: 16px;
}

.auth-form label,
.phone-fields label {
  display: grid;
  gap: 6px;
}

.auth-form label > span {
  color: rgba(237, 247, 243, 0.62);
  font-size: 11px;
  font-weight: 750;
}

.auth-form input {
  width: 100%;
  min-height: 46px;
  padding: 10px 12px;
  border: 1px solid rgba(128, 244, 214, 0.17);
  border-radius: 6px;
  color: #edf7f3;
  outline: none;
  background: rgba(13, 21, 19, 0.88);
}

.auth-form input::placeholder {
  color: rgba(237, 247, 243, 0.3);
}

.auth-form input:focus,
.password-field:focus-within {
  border-color: rgba(97, 217, 200, 0.62);
  box-shadow: 0 0 0 3px rgba(97, 217, 200, 0.09);
}

.password-field {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 46px;
  border: 1px solid rgba(128, 244, 214, 0.17);
  border-radius: 6px;
  background: rgba(13, 21, 19, 0.88);
}

.password-field input {
  border: 0;
  box-shadow: none;
  background: transparent;
}

.password-field input:focus {
  box-shadow: none;
}

.password-field button {
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  color: rgba(237, 247, 243, 0.58);
  background: transparent;
}

.phone-fields {
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 10px;
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 0 14px;
  border: 1px solid #69dec5;
  border-radius: 6px;
  color: #06110e;
  font-weight: 850;
  background: #69dec5;
  box-shadow: 0 10px 28px rgba(51, 211, 178, 0.16);
}

.submit-button:disabled {
  opacity: 0.5;
}

.preview-access {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
  color: rgba(237, 247, 243, 0.42);
  font-size: 11px;
}

.preview-access button {
  padding: 0;
  border: 0;
  color: #61d9c8;
  font-weight: 800;
  background: transparent;
}

.mission-hero {
  height: 248px;
}

.mission-hero > img {
  object-position: 52% center;
}

.mission-mask {
  justify-content: space-between;
  background:
    linear-gradient(90deg, rgba(3, 7, 6, 0.86), rgba(3, 7, 6, 0.16) 78%),
    linear-gradient(180deg, rgba(3, 7, 6, 0.18), rgba(3, 7, 6, 0.9));
}

.hero-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #67e0c7;
  font-size: 8px;
  font-weight: 900;
}

.hero-status span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.hero-status strong {
  color: rgba(237, 247, 243, 0.62);
  font-size: 9px;
}

.hero-copy {
  max-width: 310px;
}

.mission-hero h1 {
  font-size: 24px;
  line-height: 1.25;
}

.mission-hero p {
  margin: 7px 0 0;
  color: rgba(237, 247, 243, 0.66);
  font-size: 11px;
  line-height: 1.55;
}

.avatar-button {
  position: absolute;
  right: 14px;
  bottom: 14px;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 4px;
  color: #06110e;
  font-size: 14px;
  font-weight: 900;
  background: #69dec5;
}

.preview-banner {
  color: #f5cc7b;
  border: 1px solid rgba(246, 182, 72, 0.28);
  background: rgba(92, 58, 9, 0.28);
}

.data-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  color: #ffc1ae;
  border: 1px solid rgba(245, 117, 78, 0.28);
  background: rgba(110, 31, 18, 0.24);
}

.data-alert button {
  flex: 0 0 auto;
  padding: 5px 9px;
  border: 1px solid rgba(255, 193, 174, 0.3);
  border-radius: 4px;
  color: inherit;
  background: transparent;
}

.command-section,
.activity-section {
  margin-top: 24px;
}

.section-heading,
.view-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-heading {
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-heading .section-code,
.view-heading .section-code {
  margin-bottom: 2px;
}

.section-heading h2 {
  font-size: 17px;
}

.section-heading > span {
  color: rgba(237, 247, 243, 0.4);
  font-size: 9px;
  text-transform: uppercase;
}

.primary-command {
  position: relative;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 22px;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 86px;
  padding: 14px;
  overflow: hidden;
  border: 1px solid rgba(105, 222, 197, 0.34);
  border-radius: 8px;
  color: #edf7f3;
  text-align: left;
  background:
    linear-gradient(90deg, rgba(51, 211, 178, 0.14), transparent 65%),
    #0d1513;
}

.primary-command::after {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 64px;
  height: 2px;
  content: '';
  background: #69dec5;
  box-shadow: 0 0 14px #69dec5;
}

.command-icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(105, 222, 197, 0.35);
  border-radius: 6px;
  color: #69dec5;
  font-size: 21px;
  background: rgba(105, 222, 197, 0.08);
}

.command-copy small,
.command-copy strong,
.command-copy em {
  display: block;
}

.command-copy small {
  color: #69dec5;
  font-size: 8px;
  font-style: normal;
  font-weight: 850;
}

.command-copy strong {
  margin-top: 4px;
  font-size: 16px;
}

.command-copy em {
  margin-top: 4px;
  color: rgba(237, 247, 243, 0.46);
  font-size: 10px;
  font-style: normal;
  line-height: 1.4;
}

.command-arrow {
  color: #69dec5;
}

.secondary-commands {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 8px;
}

.secondary-commands button {
  position: relative;
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 12px;
  gap: 8px;
  align-items: center;
  min-height: 64px;
  padding: 11px;
  border: 1px solid rgba(237, 247, 243, 0.1);
  border-radius: 6px;
  color: #edf7f3;
  text-align: left;
  background: rgba(13, 21, 19, 0.86);
}

.secondary-commands button > .el-icon:first-child {
  color: #f6b648;
  font-size: 20px;
}

.secondary-commands small,
.secondary-commands strong {
  display: block;
}

.secondary-commands small {
  color: rgba(237, 247, 243, 0.36);
  font-size: 7px;
}

.secondary-commands strong {
  margin-top: 3px;
  font-size: 12px;
}

.secondary-commands button > i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #53605c;
}

.secondary-commands button > i.online {
  background: #65e0ad;
  box-shadow: 0 0 10px rgba(101, 224, 173, 0.8);
}

.telemetry-strip,
.robot-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 16px;
  border-top: 1px solid rgba(237, 247, 243, 0.1);
  border-bottom: 1px solid rgba(237, 247, 243, 0.1);
}

.telemetry-strip article,
.robot-summary > div {
  position: relative;
  min-width: 0;
  padding: 13px 10px;
  border-right: 1px solid rgba(237, 247, 243, 0.09);
}

.telemetry-strip article:last-child,
.robot-summary > div:last-child {
  border-right: 0;
}

.telemetry-strip span,
.robot-summary span {
  display: block;
  overflow: hidden;
  color: rgba(237, 247, 243, 0.38);
  font-size: 7px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.telemetry-strip strong,
.robot-summary strong {
  display: block;
  margin-top: 3px;
  font-size: 20px;
}

.telemetry-strip i {
  position: absolute;
  right: 10px;
  bottom: 13px;
  width: 18px;
  height: 2px;
}

.telemetry-strip i.cyan { background: #61d9c8; }
.telemetry-strip i.green { background: #65e0ad; }
.telemetry-strip i.amber { background: #f6b648; }

.icon-button,
.view-heading > button {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(237, 247, 243, 0.13);
  border-radius: 5px;
  color: rgba(237, 247, 243, 0.72);
  background: rgba(13, 21, 19, 0.86);
}

.activity-list {
  border-top: 1px solid rgba(237, 247, 243, 0.1);
}

.activity-list article {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(237, 247, 243, 0.09);
}

.activity-mark {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 1px solid;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 900;
}

.activity-mark.robot {
  color: #f6b648;
  border-color: rgba(246, 182, 72, 0.3);
  background: rgba(246, 182, 72, 0.08);
}

.activity-mark.task {
  color: #61d9c8;
  border-color: rgba(97, 217, 200, 0.3);
  background: rgba(97, 217, 200, 0.08);
}

.activity-list strong,
.activity-list small {
  display: block;
}

.activity-list strong {
  overflow: hidden;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-list small {
  margin-top: 3px;
  overflow: hidden;
  color: rgba(237, 247, 243, 0.4);
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-list em {
  max-width: 70px;
  overflow: hidden;
  color: #65e0ad;
  font-size: 9px;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  display: grid;
  justify-items: center;
  gap: 6px;
  padding: 28px 14px;
  border: 1px dashed rgba(237, 247, 243, 0.16);
  border-radius: 6px;
  color: rgba(237, 247, 243, 0.42);
  text-align: center;
  background: rgba(13, 21, 19, 0.58);
}

.empty-state .el-icon {
  color: #61d9c8;
  font-size: 23px;
}

.empty-state strong {
  color: rgba(237, 247, 243, 0.74);
  font-size: 12px;
}

.empty-state span {
  max-width: 290px;
  font-size: 10px;
  line-height: 1.5;
}

.view-heading {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 36px;
  margin-bottom: 18px;
}

.view-heading h1 {
  font-size: 20px;
}

.profile-view .view-heading {
  grid-template-columns: 36px minmax(0, 1fr);
}

.robot-summary {
  margin-top: 0;
}

.desktop-registration {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  margin-top: 16px;
  padding: 12px;
  border-left: 2px solid #f6b648;
  background: rgba(246, 182, 72, 0.07);
}

.desktop-registration > .el-icon {
  color: #f6b648;
  font-size: 23px;
}

.desktop-registration strong,
.desktop-registration span {
  display: block;
}

.desktop-registration strong {
  font-size: 12px;
}

.desktop-registration span {
  margin-top: 3px;
  color: rgba(237, 247, 243, 0.46);
  font-size: 9px;
  line-height: 1.5;
}

.desktop-registration button {
  grid-column: 1 / -1;
  min-height: 38px;
  border: 1px solid rgba(246, 182, 72, 0.3);
  border-radius: 4px;
  color: #f6c76c;
  font-size: 11px;
  font-weight: 800;
  background: rgba(246, 182, 72, 0.08);
}

.robot-list {
  display: grid;
  gap: 9px;
  margin-top: 16px;
}

.robot-list article {
  padding: 13px;
  border: 1px solid rgba(237, 247, 243, 0.11);
  border-radius: 7px;
  background: rgba(13, 21, 19, 0.82);
}

.robot-list header {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
}

.robot-glyph {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(97, 217, 200, 0.24);
  border-radius: 5px;
  color: #61d9c8;
  background: rgba(97, 217, 200, 0.06);
}

.robot-list header strong,
.robot-list header small {
  display: block;
}

.robot-list header strong {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.robot-list header small {
  margin-top: 3px;
  color: rgba(237, 247, 243, 0.38);
  font-size: 8px;
}

.robot-list header em {
  padding: 4px 6px;
  border: 1px solid;
  border-radius: 4px;
  font-size: 8px;
  font-style: normal;
}

.robot-list header em.approved { color: #65e0ad; border-color: rgba(101, 224, 173, 0.3); }
.robot-list header em.pending { color: #f6b648; border-color: rgba(246, 182, 72, 0.3); }
.robot-list header em.rejected { color: #f47f61; border-color: rgba(244, 127, 97, 0.3); }

.robot-signals {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(237, 247, 243, 0.08);
}

.robot-signals span {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(237, 247, 243, 0.38);
  font-size: 9px;
}

.robot-signals span.active {
  color: #65e0ad;
}

.robot-list article > p {
  margin: 10px 0 0;
  color: rgba(237, 247, 243, 0.48);
  font-size: 10px;
  line-height: 1.55;
}

.robot-empty {
  margin-top: 16px;
}

.robot-empty button {
  min-height: 34px;
  margin-top: 5px;
  padding: 0 12px;
  border: 1px solid rgba(97, 217, 200, 0.3);
  border-radius: 4px;
  color: #61d9c8;
  font-size: 10px;
  background: transparent;
}

.profile-identity {
  position: relative;
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) 10px;
  gap: 12px;
  align-items: center;
  padding: 17px 0;
  border-top: 1px solid rgba(237, 247, 243, 0.1);
  border-bottom: 1px solid rgba(237, 247, 243, 0.1);
}

.profile-identity > span {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border: 1px solid rgba(97, 217, 200, 0.35);
  border-radius: 5px;
  color: #06110e;
  font-size: 18px;
  font-weight: 900;
  background: #69dec5;
}

.profile-identity small,
.profile-identity strong,
.profile-identity em {
  display: block;
}

.profile-identity small {
  color: #61d9c8;
  font-size: 7px;
}

.profile-identity strong {
  margin-top: 4px;
  font-size: 16px;
}

.profile-identity em {
  margin-top: 3px;
  overflow: hidden;
  color: rgba(237, 247, 243, 0.4);
  font-size: 9px;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-identity > i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #59635f;
}

.profile-identity > i.live {
  background: #65e0ad;
  box-shadow: 0 0 12px #65e0ad;
}

.profile-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 16px;
}

.profile-metrics article {
  padding: 14px;
  border: 1px solid rgba(237, 247, 243, 0.1);
  border-radius: 6px;
  background: rgba(13, 21, 19, 0.72);
}

.profile-metrics span,
.profile-metrics strong {
  display: block;
}

.profile-metrics span {
  color: rgba(237, 247, 243, 0.4);
  font-size: 9px;
}

.profile-metrics strong {
  margin-top: 5px;
  font-size: 23px;
}

.account-list {
  margin: 20px 0 0;
  border-top: 1px solid rgba(237, 247, 243, 0.1);
}

.account-list > div {
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 12px;
  padding: 13px 0;
  border-bottom: 1px solid rgba(237, 247, 243, 0.09);
}

.account-list dt {
  color: rgba(237, 247, 243, 0.38);
  font-size: 10px;
}

.account-list dd {
  margin: 0;
  overflow-wrap: anywhere;
  color: rgba(237, 247, 243, 0.76);
  font-size: 10px;
  text-align: right;
}

.logout-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  margin-top: 22px;
  border: 1px solid rgba(244, 127, 97, 0.28);
  border-radius: 5px;
  color: #f49b84;
  background: rgba(110, 31, 18, 0.18);
  font-weight: 750;
}

.bottom-nav {
  position: fixed;
  left: 50%;
  bottom: 0;
  z-index: 40;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: min(100%, 480px);
  min-height: 66px;
  transform: translateX(-50%);
  border-top: 1px solid rgba(128, 244, 214, 0.13);
  background: rgba(7, 11, 10, 0.96);
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(16px);
}

.bottom-nav button {
  position: relative;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 4px;
  padding: 8px 4px;
  border: 0;
  color: rgba(237, 247, 243, 0.4);
  background: transparent;
}

.bottom-nav button .el-icon {
  font-size: 18px;
}

.bottom-nav button span {
  font-size: 9px;
}

.bottom-nav button.active {
  color: #69dec5;
}

.bottom-nav button.active::before {
  position: absolute;
  top: 0;
  left: 50%;
  width: 28px;
  height: 2px;
  content: '';
  transform: translateX(-50%);
  background: #69dec5;
  box-shadow: 0 0 12px #69dec5;
}

.portal-toast {
  position: fixed;
  left: 50%;
  bottom: 82px;
  z-index: 80;
  width: max-content;
  max-width: calc(100vw - 28px);
  padding: 10px 13px;
  transform: translateX(-50%);
  border: 1px solid rgba(105, 222, 197, 0.28);
  border-radius: 5px;
  color: #edf7f3;
  font-size: 11px;
  background: rgba(11, 19, 17, 0.97);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
}

.toast-pop-enter-active,
.toast-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toast-pop-enter-from,
.toast-pop-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}

@media (min-width: 620px) {
  .mobile-portal {
    padding-top: 18px;
  }

  .portal-bar {
    width: min(calc(100% - 32px), 480px);
    margin: 0 auto;
    border: 1px solid rgba(128, 244, 214, 0.13);
    border-radius: 8px 8px 0 0;
  }

  .portal-shell {
    min-height: calc(100vh - 100px);
    border-right: 1px solid rgba(128, 244, 214, 0.1);
    border-left: 1px solid rgba(128, 244, 214, 0.1);
    background: rgba(7, 11, 10, 0.88);
  }

  .bottom-nav {
    bottom: 18px;
    border: 1px solid rgba(128, 244, 214, 0.13);
    border-radius: 0 0 8px 8px;
  }
}

@media (max-width: 360px) {
  .portal-shell {
    padding-right: 10px;
    padding-left: 10px;
  }

  .auth-visual {
    min-height: 250px;
  }

  .secondary-commands {
    grid-template-columns: 1fr;
  }

  .desktop-registration span {
    max-width: 240px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation: none !important;
    transition: none !important;
  }
}
</style>
